import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

export interface DashboardParameterObjectItem {
  label: string;
  type: string;
}

export interface DashboardParameterObjectEntry extends DashboardParameterObjectItem {
  key: string;
}

interface DashboardParameterObjectsModalData {
  dashboard_name: string;
  parameters_objects: Record<string, DashboardParameterObjectItem>;
}

interface ParameterFieldConfig {
  control_name: 'label' | 'type';
  label: string;
  input_type: 'text' | 'select';
  placeholder?: string;
  options?: Array<{ value: string; view_value: string }>;
}

@Component({
  selector: 'app-dashboard-parameter-objects-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './dashboard-parameter-objects-modal.component.html',
  styleUrl: './dashboard-parameter-objects-modal.component.scss'
})
export class DashboardParameterObjectsModalComponent {
  readonly field_config: ParameterFieldConfig[] = [
    {
      control_name: 'label',
      label: 'Label',
      input_type: 'text',
      placeholder: 'sdv'
    },
    {
      control_name: 'type',
      label: 'Tipo',
      input_type: 'select',
      options: [
        { value: 'string', view_value: 'String' },
        { value: 'number', view_value: 'Number' },
        { value: 'boolean', view_value: 'Boolean' }
      ]
    }
  ];

  readonly form_group: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialog_ref: MatDialogRef<DashboardParameterObjectsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: DashboardParameterObjectsModalData
  ) {
    this.form_group = this.fb.group({
      items: this.fb.array(this.build_items())
    });
  }

  get items_array(): FormArray<FormGroup> {
    // Expone el arreglo de filas del modal.
    return this.form_group.get('items') as FormArray<FormGroup>;
  }

  get has_items(): boolean {
    // Indica si hay filas para mostrar en el modal.
    return this.items_array.length > 0;
  }

  add_item(): void {
    // Agrega una fila vacía al formulario.
    this.items_array.push(this.create_item_group());
  }

  remove_item(index: number): void {
    // Elimina una fila del formulario.
    this.items_array.removeAt(index);
  }

  save(): void {
    // Normaliza el formulario y devuelve el objeto final.
    if (this.form_group.invalid) {
      this.form_group.markAllAsTouched();
      return;
    }

    const entries = this.normalize_items(this.items_array.getRawValue());
    const payload = entries.reduce((acc, entry) => {
      {
      acc[entry.key] = {
        label: entry.label,
        type: entry.type
      }};
      return acc;
    }, {} as Record<string, DashboardParameterObjectItem>);
    console.log('Saving parameter objects:', payload);
    this.dialog_ref.close(payload);
  }

  close(): void {
    // Cierra el modal sin guardar cambios.
    this.dialog_ref.close();
  }

  preview_key(index: number): string {
    // Muestra la clave actual o sugiere una basada en el label.
    const control_key = String(this.items_array.at(index)?.get('key')?.value ?? '').trim();
    if (control_key.length > 0) {
      return control_key;
    }

    const raw_label = String(this.items_array.at(index)?.get('label')?.value ?? '').trim();
    return this.unique_key(raw_label, index);
  }

  get_field_config(control_name: 'label' | 'type'): ParameterFieldConfig | undefined {
    // Devuelve la configuración visual de un campo.
    return this.field_config.find((field) => field.control_name === control_name);
  }

  is_text_field(control_name: 'label' | 'type'): boolean {
    // Indica si un campo debe pintarse como input de texto.
    return this.get_field_config(control_name)?.input_type === 'text';
  }

  is_select_field(control_name: 'label' | 'type'): boolean {
    // Indica si un campo debe pintarse como selector.
    return this.get_field_config(control_name)?.input_type === 'select';
  }

  get_control_error(control: AbstractControl | null, error_name: string): boolean {
    // Centraliza la lectura de errores del formulario.
    return Boolean(control?.hasError(error_name) && (control.dirty || control.touched));
  }

  get_options(control_name: 'label' | 'type'): Array<{ value: string; view_value: string }> {
    // Retorna las opciones del selector configurado.
    return this.get_field_config(control_name)?.options ?? [];
  }

  private build_items(): FormGroup[] {
    // Convierte el objeto recibido en filas editables.
    return Object.entries(this.data.parameters_objects ?? {}).map(([key, item]) =>
      this.create_item_group({
        key,
        label: item?.label ?? key,
        type: item?.type ?? 'string'
      })
    );
  }

  private create_item_group(entry?: Partial<DashboardParameterObjectEntry>): FormGroup {
    // Crea una fila del formulario usando la configuración de campos.
    return this.fb.group({
      key: [entry?.key ?? ''],
      label: [entry?.label ?? '', [Validators.required, Validators.minLength(2)]],
      type: [entry?.type ?? 'string', Validators.required]
    });
  }

  private normalize_items(items: unknown): DashboardParameterObjectEntry[] {
    // Limpia filas vacías y genera keys estables.
    if (!Array.isArray(items)) {
      return [];
    }

    const used_keys = new Set<string>();

    return items
      .map((item, index) => {
        const raw_label = String((item as DashboardParameterObjectEntry)?.label ?? '').trim();
        const raw_type = String((item as DashboardParameterObjectEntry)?.type ?? 'string').trim() || 'string';
        const raw_key = String((item as DashboardParameterObjectEntry)?.key ?? '').trim();
        const key = this.unique_key(raw_key || raw_label, index, used_keys);

        used_keys.add(key);
        return {
          key,
          label: raw_label,
          type: raw_type
        };
      })
      .filter((item) => item.label.length > 0);
  }

  private unique_key(base_value: string, index: number, used_keys?: Set<string>): string {
    // Genera una clave legible y evita duplicados.
    const normalized_base = this.slugify(base_value);
    const existing = used_keys ?? new Set<string>();
    let candidate = normalized_base || `parameter_${index + 1}`;
    let suffix = 1;

    while (existing.has(candidate)) {
      candidate = `${normalized_base || 'parameter'}_${suffix}`;
      suffix += 1;
    }

    return candidate;
  }

  private slugify(value: string): string {
    // Convierte un label en una clave simple.
    const normalized = String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    return normalized || 'parameter';
  }
}
